export const STORAGE_KEY = 'home-workout-pacer';
export const CURRENT_VERSION = 2;

export type Effort = 'easy' | 'right' | 'hard';
export interface Session {
  id: string;
  completedAt: string;
  level: number;
  rounds: number;
  durationSeconds: number;
  effort: Effort;
}
export interface Preferences {
  muted: boolean;
  voiceURI: string;
  speechRate: number;
  manualLevel: number | null;
}
export interface AppData {
  version: 2;
  sessions: Session[];
  preferences: Preferences;
}

export const defaults: AppData = {
  version: 2,
  sessions: [],
  preferences: { muted: false, voiceURI: '', speechRate: 1, manualLevel: null },
};

function validSession(value: unknown): value is Session {
  if (!value || typeof value !== 'object') return false;
  const s = value as Partial<Session>;
  return typeof s.id === 'string' && typeof s.completedAt === 'string' &&
    typeof s.level === 'number' && typeof s.rounds === 'number' &&
    typeof s.durationSeconds === 'number' && ['easy', 'right', 'hard'].includes(String(s.effort));
}

export function migrate(raw: unknown): AppData {
  if (!raw || typeof raw !== 'object') return structuredClone(defaults);
  const source = raw as Record<string, unknown>;
  const oldPrefs = (source.preferences && typeof source.preferences === 'object' ? source.preferences : source) as Record<string, unknown>;
  const sessions = Array.isArray(source.sessions) ? source.sessions.filter(validSession).slice(-100) : [];
  const rate = typeof oldPrefs.speechRate === 'number' ? oldPrefs.speechRate : 1;
  const manualLevel = Number.isInteger(oldPrefs.manualLevel) ? Math.max(0, Math.min(4, Number(oldPrefs.manualLevel))) : null;
  return {
    version: CURRENT_VERSION,
    sessions,
    preferences: {
      muted: Boolean(oldPrefs.muted),
      voiceURI: typeof oldPrefs.voiceURI === 'string' ? oldPrefs.voiceURI : '',
      speechRate: Math.max(0.8, Math.min(1.2, rate)),
      manualLevel,
    },
  };
}

export function loadData(storage: Pick<Storage, 'getItem'> = localStorage): AppData {
  try {
    const value = storage.getItem(STORAGE_KEY);
    return value ? migrate(JSON.parse(value)) : structuredClone(defaults);
  } catch {
    return structuredClone(defaults);
  }
}

export function saveData(data: AppData, storage: Pick<Storage, 'setItem'> = localStorage): void {
  try { storage.setItem(STORAGE_KEY, JSON.stringify(migrate(data))); } catch { /* Private mode or full storage: app still works. */ }
}
